/**
 * GitLab-Job-Logs (Trace) zerlegen und auswerten.
 *
 * Reine Logik ohne DB: ANSI-Escape-Sequenzen werden in farbige Segmente
 * zerlegt, Log-Zeilen heuristisch als Fehler/Erfolg/Warnung eingestuft. Die
 * Oberfläche rendert daraus eine durchsuchbare, farbige Log-Ansicht.
 */
const ansiCodeZuFarbe = {
    30: "schwarz",
    31: "rot",
    32: "gruen",
    33: "gelb",
    34: "blau",
    35: "magenta",
    36: "cyan",
    37: "weiss",
    90: "hellgrau",
    91: "hellrot",
    92: "hellgruen",
    93: "hellgelb",
    94: "hellblau",
    95: "hellmagenta",
    96: "hellcyan",
    97: "hellweiss",
};
/**
 * Zerlegt Text mit ANSI-SGR-Sequenzen (`\x1b[…m`) in farbige Segmente.
 * Nicht-Farbcodes (fett, unterstrichen, …) werden ignoriert; `0` setzt die
 * Farbe zurück.
 */
export function zerlegeAnsi(text) {
    const segmente = [];
    let aktuelleFarbe = null;
    let puffer = "";
    const schliesse = () => {
        if (puffer !== "") {
            segmente.push({ text: puffer, farbe: aktuelleFarbe });
            puffer = "";
        }
    };
    const muster = /\u001b\[([0-9;]*)m/g;
    let letzterIndex = 0;
    let treffer;
    while ((treffer = muster.exec(text)) !== null) {
        if (treffer.index > letzterIndex) {
            puffer += text.slice(letzterIndex, treffer.index);
        }
        const parameter = treffer[1] ?? "";
        if (parameter === "" || parameter === "0") {
            schliesse();
            aktuelleFarbe = null;
        }
        else {
            const codes = parameter.split(";").map((c) => Number(c));
            const farbCode = codes.find((c) => (c >= 30 && c <= 37) || (c >= 90 && c <= 97));
            if (farbCode !== undefined) {
                schliesse();
                aktuelleFarbe = ansiCodeZuFarbe[farbCode] ?? null;
            }
        }
        letzterIndex = muster.lastIndex;
    }
    if (letzterIndex < text.length) {
        puffer += text.slice(letzterIndex);
    }
    schliesse();
    return segmente;
}
/**
 * Stuft eine Log-Zeile heuristisch ein — für Test-Logs typische Marker.
 * Bewusst konservativ, damit „ok“ in Fließtext nicht alles grün färbt.
 */
export function klassifiziereLogZeile(text) {
    const klar = text.trim();
    if (/✗|×|FAIL|failed|Failure|AssertionError|Traceback|Exception|Error:|ERROR/.test(klar)) {
        return "fehler";
    }
    if (/✓|PASS|passed|SUCCESS|successfully|^ok$|ok \(|Tests? passed/.test(klar)) {
        return "erfolg";
    }
    if (/WARN|warning/i.test(klar)) {
        return "warnung";
    }
    return "normal";
}
/** Zerlegt ein Trace in nummerierte, klassifizierte Zeilen. */
export function zerlegeLog(text) {
    const roh = text.split(/\r?\n/);
    // Abschließende Leerzeile aus dem letzten Zeilenende entfernen.
    if (roh.at(-1) === "")
        roh.pop();
    return roh.map((zeile, index) => {
        const segmente = zerlegeAnsi(zeile);
        return {
            nummer: index + 1,
            segmente,
            art: klassifiziereLogZeile(segmente.map((s) => s.text).join("")),
        };
    });
}
export function fasseLogZusammen(zeilen) {
    let fehler = 0;
    let erfolg = 0;
    let warnungen = 0;
    for (const zeile of zeilen) {
        if (zeile.art === "fehler")
            fehler += 1;
        if (zeile.art === "erfolg")
            erfolg += 1;
        if (zeile.art === "warnung")
            warnungen += 1;
    }
    return { zeilen: zeilen.length, fehler, erfolg, warnungen };
}
//# sourceMappingURL=log.js.map